import { useReducer, useEffect, useCallback, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Globe,
  Download,
  Sparkles,
  Link2,
  Rocket,
  Loader2,
  Check,
  SkipForward,
  ExternalLink,
} from "lucide-react";
import {
  agentTasks,
  sites,
  businesses as businessesApi,
  type Business,
} from "../lib/api";
import { usePolling } from "../hooks/use-polling";
import BuildProgressTracker from "../components/BuildProgressTracker";

// ─── State ──────────────────────────────────────────────

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface WizardState {
  step: WizardStep;
  domain: string;
  niche: string;
  sourceUrl: string;
  businessId: string;
  crawlTaskId: string | null;
  crawlStatus: "idle" | "crawling" | "done" | "error";
  crawlError: string | null;
  importedPageCount: number;
  taxonomyTaskId: string | null;
  taxonomyStatus: "idle" | "loading" | "done" | "error";
  taxonomyError: string | null;
  taxonomyResult: { categories: number; brands: number; models: number } | null;
  affiliatePrograms: string[];
  affiliateConfig: {
    ebay: { campaignId: string; publisherId: string; region: string };
    amazon: { trackingId: string; region: string };
    awin: { publisherId: string };
    cj: { websiteId: string };
  };
  siteId: string | null;
  buildTaskId: string | null;
  buildStatus: "idle" | "building" | "done" | "error";
}

type Action =
  | { type: "SET_STEP"; step: WizardStep }
  | { type: "SET_FIELD"; field: keyof WizardState; value: unknown }
  | { type: "SET_CRAWL_STARTED"; taskId: string }
  | { type: "SET_CRAWL_DONE"; pageCount: number }
  | { type: "SET_CRAWL_ERROR"; error: string }
  | { type: "SET_TAXONOMY_STARTED"; taskId: string }
  | { type: "SET_TAXONOMY_DONE"; result: { categories: number; brands: number; models: number } }
  | { type: "SET_TAXONOMY_ERROR"; error: string }
  | { type: "SET_SITE_CREATED"; siteId: string }
  | { type: "SET_BUILD_STARTED"; taskId: string }
  | { type: "SET_BUILD_DONE" }
  | { type: "TOGGLE_PROGRAM"; program: string }
  | { type: "SET_AFFILIATE_FIELD"; program: string; field: string; value: string };

const initialState: WizardState = {
  step: 1,
  domain: "",
  niche: "",
  sourceUrl: "",
  businessId: "",
  crawlTaskId: null,
  crawlStatus: "idle",
  crawlError: null,
  importedPageCount: 0,
  taxonomyTaskId: null,
  taxonomyStatus: "idle",
  taxonomyError: null,
  taxonomyResult: null,
  affiliatePrograms: [],
  affiliateConfig: {
    ebay: { campaignId: "", publisherId: "", region: "US" },
    amazon: { trackingId: "", region: "US" },
    awin: { publisherId: "" },
    cj: { websiteId: "" },
  },
  siteId: null,
  buildTaskId: null,
  buildStatus: "idle",
};

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "SET_CRAWL_STARTED":
      return { ...state, crawlTaskId: action.taskId, crawlStatus: "crawling", crawlError: null };
    case "SET_CRAWL_DONE":
      return { ...state, crawlStatus: "done", importedPageCount: action.pageCount };
    case "SET_CRAWL_ERROR":
      return { ...state, crawlStatus: "error", crawlError: action.error };
    case "SET_TAXONOMY_STARTED":
      return { ...state, taxonomyTaskId: action.taskId, taxonomyStatus: "loading", taxonomyError: null };
    case "SET_TAXONOMY_DONE":
      return { ...state, taxonomyStatus: "done", taxonomyResult: action.result };
    case "SET_TAXONOMY_ERROR":
      return { ...state, taxonomyStatus: "error", taxonomyError: action.error };
    case "SET_SITE_CREATED":
      return { ...state, siteId: action.siteId };
    case "SET_BUILD_STARTED":
      return { ...state, buildTaskId: action.taskId, buildStatus: "building" };
    case "SET_BUILD_DONE":
      return { ...state, buildStatus: "done" };
    case "TOGGLE_PROGRAM": {
      const progs = state.affiliatePrograms.includes(action.program)
        ? state.affiliatePrograms.filter((p) => p !== action.program)
        : [...state.affiliatePrograms, action.program];
      return { ...state, affiliatePrograms: progs };
    }
    case "SET_AFFILIATE_FIELD": {
      const prog = action.program as keyof WizardState["affiliateConfig"];
      return {
        ...state,
        affiliateConfig: {
          ...state.affiliateConfig,
          [prog]: {
            ...state.affiliateConfig[prog],
            [action.field]: action.value,
          },
        },
      };
    }
    default:
      return state;
  }
}

// ─── Step Indicator ─────────────────────────────────────

const STEPS = [
  { num: 1, label: "Domain", icon: Globe },
  { num: 2, label: "Import", icon: Download },
  { num: 3, label: "Taxonomy", icon: Sparkles },
  { num: 4, label: "Affiliates", icon: Link2 },
  { num: 5, label: "Build", icon: Rocket },
] as const;

function StepIndicator({ current }: { current: WizardStep }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map(({ num, label, icon: Icon }, i) => {
        const isActive = num === current;
        const isDone = num < current;
        return (
          <div key={num} className="flex items-center">
            {i > 0 && (
              <div
                className={`w-12 h-px mx-1 ${
                  isDone ? "bg-green-500" : "bg-zinc-700"
                }`}
              />
            )}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                  isDone
                    ? "bg-green-500/20 border-green-500 text-green-400"
                    : isActive
                    ? "bg-blue-500/20 border-blue-500 text-blue-400"
                    : "bg-zinc-800 border-zinc-700 text-zinc-500"
                }`}
              >
                {isDone ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-xs font-medium ${
                  isDone
                    ? "text-green-400"
                    : isActive
                    ? "text-blue-400"
                    : "text-zinc-500"
                }`}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Task Poller Hook ─────────────────────────────────────

function useTaskPoller(
  taskId: string | null,
  onDone: (task: { result?: Record<string, unknown> }) => void,
  onError: (msg: string) => void,
) {
  const poll = useCallback(async () => {
    if (!taskId) return;
    try {
      const res = await agentTasks.get(taskId);
      const t = res.data;
      if (t.status === "done") {
        onDone(t);
      } else if (t.status === "failed") {
        onError(t.error ?? "Task failed");
      }
    } catch {
      // silent
    }
  }, [taskId, onDone, onError]);

  usePolling(poll, 5_000);
}

// ─── Main Component ─────────────────────────────────────

export default function CreateWebsite() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [businessList, setBusinessList] = useState<Business[]>([]);
  const [creating, setCreating] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Load businesses for the optional select
  useEffect(() => {
    businessesApi
      .list()
      .then((res) => setBusinessList(res.data))
      .catch(() => {});
  }, []);

  // Poll crawl task
  useTaskPoller(
    state.crawlStatus === "crawling" ? state.crawlTaskId : null,
    useCallback(
      (t) => {
        const pageCount =
          (t.result?.imported as number) ??
          (t.result?.pageCount as number) ??
          0;
        dispatch({ type: "SET_CRAWL_DONE", pageCount });
      },
      [],
    ),
    useCallback((msg: string) => dispatch({ type: "SET_CRAWL_ERROR", error: msg }), []),
  );

  // Poll taxonomy task
  useTaskPoller(
    state.taxonomyStatus === "loading" ? state.taxonomyTaskId : null,
    useCallback(
      (t) => {
        const r = t.result ?? {};
        dispatch({
          type: "SET_TAXONOMY_DONE",
          result: {
            categories: (r.categories as number) ?? 0,
            brands: (r.brands as number) ?? 0,
            models: (r.models as number) ?? 0,
          },
        });
      },
      [],
    ),
    useCallback((msg: string) => dispatch({ type: "SET_TAXONOMY_ERROR", error: msg }), []),
  );

  // ─── Step handlers ──────────────────────────────────────

  async function handleStep1Next() {
    if (!state.domain.trim() || !state.niche.trim()) {
      setStepError("Domain and niche are required.");
      return;
    }
    setStepError(null);
    setCreating(true);
    try {
      const res = await sites.create({
        domain: state.domain.trim(),
        config: {
          title: state.domain.trim(),
          description: "",
          niche: state.niche.trim(),
          affiliatePrograms: [],
          interlinks: [],
          ...(state.sourceUrl.trim() ? { sourceUrl: state.sourceUrl.trim() } : {}),
        },
        ...(state.businessId ? { clusterId: state.businessId } : {}),
      });
      dispatch({ type: "SET_SITE_CREATED", siteId: res.data._id });
      dispatch({ type: "SET_STEP", step: 2 });
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : "Failed to create site");
    } finally {
      setCreating(false);
    }
  }

  async function handleStartImport() {
    if (!state.siteId) return;
    setStepError(null);
    try {
      const url = state.sourceUrl.trim() || `https://${state.domain}`;
      const res = await agentTasks.importContent(state.siteId, { sourceUrl: url });
      dispatch({ type: "SET_CRAWL_STARTED", taskId: res.data._id });
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : "Failed to start import");
    }
  }

  async function handleStartTaxonomy() {
    if (!state.siteId) return;
    setStepError(null);
    try {
      const res = await agentTasks.discoverTaxonomy(state.siteId);
      const taskId = (res as { data?: { _id?: string }; _id?: string }).data?._id ?? (res as { _id?: string })._id ?? "";
      dispatch({ type: "SET_TAXONOMY_STARTED", taskId });
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : "Failed to start taxonomy discovery");
    }
  }

  async function handleSaveAffiliates() {
    if (!state.siteId) return;
    setStepError(null);
    try {
      const overrides: Record<string, unknown> = {};
      for (const prog of state.affiliatePrograms) {
        const cfg = state.affiliateConfig[prog as keyof WizardState["affiliateConfig"]];
        if (cfg) {
          const cleaned: Record<string, string> = {};
          for (const [k, v] of Object.entries(cfg)) {
            if (v) cleaned[k] = v;
          }
          if (Object.keys(cleaned).length > 0) overrides[prog] = cleaned;
        }
      }
      await sites.update(state.siteId, {
        config: {
          affiliatePrograms: state.affiliatePrograms,
          affiliateOverrides: overrides,
        },
      } as unknown as Partial<import("../lib/api").Site>);
      dispatch({ type: "SET_STEP", step: 5 });
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : "Failed to save affiliate config");
    }
  }

  async function handleLaunchBuild() {
    if (!state.siteId) return;
    setStepError(null);
    try {
      const res = await agentTasks.createWebsite({
        domain: state.domain,
        niche: state.niche,
        ...(state.sourceUrl ? { sourceUrl: state.sourceUrl } : {}),
        ...(state.businessId ? { businessId: state.businessId } : {}),
        ...(state.affiliatePrograms.length > 0 ? { affiliatePrograms: state.affiliatePrograms } : {}),
      });
      const taskId = (res as { task?: { _id?: string } }).task?._id ?? "";
      const siteId = (res as { site?: { _id?: string } }).site?._id ?? state.siteId;
      dispatch({ type: "SET_FIELD", field: "siteId", value: siteId });
      dispatch({ type: "SET_BUILD_STARTED", taskId });
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : "Failed to launch build");
    }
  }

  // Auto-start taxonomy when entering step 3
  useEffect(() => {
    if (state.step === 3 && state.taxonomyStatus === "idle") {
      void handleStartTaxonomy();
    }
  }, [state.step]);

  function goBack() {
    if (state.step > 1) {
      dispatch({ type: "SET_STEP", step: (state.step - 1) as WizardStep });
      setStepError(null);
    }
  }

  // ─── Render ────────────────────────────────────────────

  return (
    <div className="min-h-screen p-6">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-zinc-100 text-center mb-2">
          Create New Website
        </h1>
        <p className="text-sm text-zinc-500 text-center mb-8">
          Set up a new affiliate site with guided configuration
        </p>

        <StepIndicator current={state.step} />

        {/* Error display */}
        {stepError && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
            {stepError}
            <button onClick={() => setStepError(null)} className="ml-2 text-red-500 hover:text-red-300">
              &times;
            </button>
          </div>
        )}

        {/* ═════════ Step 1: Domain & Niche ═════════ */}
        {state.step === 1 && (
          <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                Domain *
              </label>
              <input
                type="text"
                value={state.domain}
                onChange={(e) =>
                  dispatch({ type: "SET_FIELD", field: "domain", value: e.target.value })
                }
                placeholder="example.com"
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                Niche *
              </label>
              <input
                type="text"
                value={state.niche}
                onChange={(e) =>
                  dispatch({ type: "SET_FIELD", field: "niche", value: e.target.value })
                }
                placeholder="e.g. outdoor camping gear"
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                Business <span className="text-zinc-600">(optional)</span>
              </label>
              <select
                value={state.businessId}
                onChange={(e) =>
                  dispatch({ type: "SET_FIELD", field: "businessId", value: e.target.value })
                }
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">None</option>
                {businessList.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                Source URL <span className="text-zinc-600">(optional, defaults to https://domain)</span>
              </label>
              <input
                type="text"
                value={state.sourceUrl}
                onChange={(e) =>
                  dispatch({ type: "SET_FIELD", field: "sourceUrl", value: e.target.value })
                }
                placeholder={`https://${state.domain || "example.com"}`}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleStep1Next}
                disabled={creating}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {creating ? "Creating..." : "Next"}
              </button>
            </div>
          </div>
        )}

        {/* ═════════ Step 2: Import Content ═════════ */}
        {state.step === 2 && (
          <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-zinc-100 mb-1">
                Import Existing Content
              </h2>
              <p className="text-sm text-zinc-500">
                Crawl and import pages from{" "}
                <span className="text-zinc-300">
                  {state.sourceUrl || `https://${state.domain}`}
                </span>
              </p>
            </div>

            {state.crawlStatus === "idle" && (
              <button
                onClick={handleStartImport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Start Import
              </button>
            )}

            {state.crawlStatus === "crawling" && (
              <div className="flex items-center gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                <span className="text-sm text-blue-400">
                  Crawling and importing pages...
                </span>
              </div>
            )}

            {state.crawlStatus === "done" && (
              <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-sm text-green-400">
                  Imported {state.importedPageCount} pages
                </span>
              </div>
            )}

            {state.crawlStatus === "error" && (
              <div className="space-y-3">
                <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {state.crawlError ?? "Import failed"}
                </div>
                <button
                  onClick={handleStartImport}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Retry Import
                </button>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <button
                onClick={goBack}
                className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <div className="flex gap-3">
                {state.crawlStatus !== "done" && state.crawlStatus !== "crawling" && (
                  <button
                    onClick={() => {
                      dispatch({ type: "SET_STEP", step: 3 });
                      setStepError(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded-lg transition-colors"
                  >
                    <SkipForward className="w-4 h-4" />
                    Skip
                  </button>
                )}
                {state.crawlStatus === "done" && (
                  <button
                    onClick={() => {
                      dispatch({ type: "SET_STEP", step: 3 });
                      setStepError(null);
                    }}
                    className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═════════ Step 3: Discover Taxonomy ═════════ */}
        {state.step === 3 && (
          <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-zinc-100 mb-1">
                Discover Taxonomy
              </h2>
              <p className="text-sm text-zinc-500">
                AI analyzes your content to discover categories, brands, and product models
              </p>
            </div>

            {state.taxonomyStatus === "loading" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                  <span className="text-sm text-blue-400">
                    Discovering taxonomy...
                  </span>
                </div>
                {/* Loading skeleton */}
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-zinc-900 rounded-lg p-4 animate-pulse">
                      <div className="h-3 w-16 bg-zinc-700 rounded mb-3" />
                      <div className="h-6 w-10 bg-zinc-700 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {state.taxonomyStatus === "done" && state.taxonomyResult && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Categories", value: state.taxonomyResult.categories, color: "text-blue-400" },
                  { label: "Brands", value: state.taxonomyResult.brands, color: "text-emerald-400" },
                  { label: "Models", value: state.taxonomyResult.models, color: "text-amber-400" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 mb-1">{label}</p>
                    <p className={`text-xl font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            )}

            {state.taxonomyStatus === "error" && (
              <div className="space-y-3">
                <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {state.taxonomyError ?? "Taxonomy discovery failed"}
                </div>
                <button
                  onClick={handleStartTaxonomy}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Retry
                </button>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <button
                onClick={goBack}
                className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              {(state.taxonomyStatus === "done" || state.taxonomyStatus === "error") && (
                <button
                  onClick={() => {
                    dispatch({ type: "SET_STEP", step: 4 });
                    setStepError(null);
                  }}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ═════════ Step 4: Affiliate Config ═════════ */}
        {state.step === 4 && (
          <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-zinc-100 mb-1">
                Affiliate Programs
              </h2>
              <p className="text-sm text-zinc-500">
                Select and configure which affiliate programs to use
              </p>
            </div>

            {/* Program checkboxes */}
            <div className="grid grid-cols-2 gap-3">
              {(["ebay", "amazon", "awin", "cj"] as const).map((prog) => {
                const labels: Record<string, string> = {
                  ebay: "eBay Partner Network",
                  amazon: "Amazon Associates",
                  awin: "AWIN",
                  cj: "CJ Affiliate",
                };
                const selected = state.affiliatePrograms.includes(prog);
                return (
                  <button
                    key={prog}
                    type="button"
                    onClick={() => dispatch({ type: "TOGGLE_PROGRAM", program: prog })}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left text-sm transition-colors ${
                      selected
                        ? "border-blue-500/50 bg-blue-500/10 text-blue-400"
                        : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        selected ? "bg-blue-500 border-blue-500" : "border-zinc-600"
                      }`}
                    >
                      {selected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    {labels[prog]}
                  </button>
                );
              })}
            </div>

            {/* Config fields for selected programs */}
            {state.affiliatePrograms.includes("ebay") && (
              <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                  eBay Config
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Campaign ID</label>
                    <input
                      value={state.affiliateConfig.ebay.campaignId}
                      onChange={(e) =>
                        dispatch({ type: "SET_AFFILIATE_FIELD", program: "ebay", field: "campaignId", value: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Region</label>
                    <select
                      value={state.affiliateConfig.ebay.region}
                      onChange={(e) =>
                        dispatch({ type: "SET_AFFILIATE_FIELD", program: "ebay", field: "region", value: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {["US", "UK", "DE", "AU", "CA", "FR", "IT", "ES"].map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {state.affiliatePrograms.includes("amazon") && (
              <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                  Amazon Config
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Tracking ID</label>
                    <input
                      value={state.affiliateConfig.amazon.trackingId}
                      onChange={(e) =>
                        dispatch({ type: "SET_AFFILIATE_FIELD", program: "amazon", field: "trackingId", value: e.target.value })
                      }
                      placeholder="yourstore-20"
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Region</label>
                    <select
                      value={state.affiliateConfig.amazon.region}
                      onChange={(e) =>
                        dispatch({ type: "SET_AFFILIATE_FIELD", program: "amazon", field: "region", value: e.target.value })
                      }
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {["US", "UK", "DE", "CA", "FR", "IT", "ES", "JP", "AU", "IN"].map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {state.affiliatePrograms.includes("awin") && (
              <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                  AWIN Config
                </h4>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Publisher ID</label>
                  <input
                    value={state.affiliateConfig.awin.publisherId}
                    onChange={(e) =>
                      dispatch({ type: "SET_AFFILIATE_FIELD", program: "awin", field: "publisherId", value: e.target.value })
                    }
                    className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {state.affiliatePrograms.includes("cj") && (
              <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
                  CJ Affiliate Config
                </h4>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Website ID</label>
                  <input
                    value={state.affiliateConfig.cj.websiteId}
                    onChange={(e) =>
                      dispatch({ type: "SET_AFFILIATE_FIELD", program: "cj", field: "websiteId", value: e.target.value })
                    }
                    className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <button
                onClick={goBack}
                className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleSaveAffiliates}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ═════════ Step 5: Confirm & Build ═════════ */}
        {state.step === 5 && (
          <div className="space-y-5">
            {state.buildStatus === "idle" && (
              <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 space-y-5">
                <h2 className="text-base font-semibold text-zinc-100">
                  Review & Launch
                </h2>

                {/* Summary */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-zinc-700">
                    <span className="text-sm text-zinc-500">Domain</span>
                    <span className="text-sm text-zinc-200 font-medium">{state.domain}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-zinc-700">
                    <span className="text-sm text-zinc-500">Niche</span>
                    <span className="text-sm text-zinc-200 font-medium">{state.niche}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-zinc-700">
                    <span className="text-sm text-zinc-500">Imported Pages</span>
                    <span className="text-sm text-zinc-200 font-medium">{state.importedPageCount || "0"}</span>
                  </div>
                  {state.taxonomyResult && (
                    <div className="flex items-center justify-between py-2 border-b border-zinc-700">
                      <span className="text-sm text-zinc-500">Taxonomy</span>
                      <span className="text-sm text-zinc-200 font-medium">
                        {state.taxonomyResult.categories} categories, {state.taxonomyResult.brands} brands, {state.taxonomyResult.models} models
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-zinc-500">Affiliate Programs</span>
                    <span className="text-sm text-zinc-200 font-medium">
                      {state.affiliatePrograms.length > 0
                        ? state.affiliatePrograms.join(", ")
                        : "None"}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleLaunchBuild}
                    className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <Rocket className="w-4 h-4" />
                    Launch Build
                  </button>
                </div>
              </div>
            )}

            {/* Build in progress */}
            {state.buildTaskId && state.buildStatus !== "idle" && (
              <div className="space-y-4">
                <BuildProgressTracker
                  taskId={state.buildTaskId}
                  onComplete={() => dispatch({ type: "SET_BUILD_DONE" })}
                />

                {state.buildStatus === "done" && state.siteId && (
                  <div className="flex justify-center">
                    <Link
                      to={`/websites/${state.siteId}`}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Go to Site Dashboard
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
